// src/features/system/devices/components/iot/IoTDeviceLogPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Displays device-side log entries ingested through the hot-stream pipeline.
//
// Data flows:   ESP32 Logger → /iot/ingest (category=log) → hot_stream
//               (scope_ns=iot.device.log, id_category=3)
//
// Features:
//   • Device selector + level filter (TRACE → ERROR)
//   • Auto-refresh every 10 s
//   • Color-coded level badges (PAC neon accent palette)
//   • Monospace log output with tag + message
// ────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import {
  fetchDeviceLogsApi,
  DEVICE_KEYS,
  type Device,
  type DeviceLogEntry,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';

/* ── Constants ───────────────────────────────────────────────────────── */

const LOG_LEVELS = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR'] as const;

const LEVEL_COLORS: Record<string, string> = {
  TRACE: 'text-neutral-500',
  DEBUG: 'text-sky-400',
  INFO:  'text-emerald-400',
  WARN:  'text-orange-400',
  ERROR: 'text-red-400',
};

const LEVEL_BG: Record<string, string> = {
  TRACE: 'bg-neutral-700/40',
  DEBUG: 'bg-sky-900/30',
  INFO:  'bg-emerald-900/30',
  WARN:  'bg-orange-900/30',
  ERROR: 'bg-red-900/30',
};

/* ── Helpers ─────────────────────────────────────────────────────────── */

function formatTs(v: string): string {
  try {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
  } catch { return v; }
}

function timeAgo(v: string): string {
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 0) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch { return v; }
}

function formatDeviceTs(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h${String(m).padStart(2, '0')}m${String(sec).padStart(2, '0')}s`;
}

/* ── Component ───────────────────────────────────────────────────────── */

export default function IoTDeviceLogPanel() {
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [levelFilter, setLevelFilter]       = useState<string>('');
  const [limit, setLimit]                   = useState(200);

  /* Device list for selector */
  const { data: devices = [] } = useDevicesList();

  /* Log entries for selected device */
  const {
    data: logs = [],
    isLoading,
    error,
    isFetching,
  } = useQuery<DeviceLogEntry[], Error>({
    queryKey: [...DEVICE_KEYS.logs(selectedDevice ?? ''), levelFilter, limit],
    queryFn:  () => fetchDeviceLogsApi({
      device_id: selectedDevice!,
      level:     levelFilter || undefined,
      limit,
    }),
    enabled:         !!selectedDevice,
    refetchInterval: 10_000,
  });

  /* Summary stats */
  const stats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const l of logs) {
      counts[l.level] = (counts[l.level] ?? 0) + 1;
    }
    return counts;
  }, [logs]);

  /* Table columns */
  const columns: ColumnDef<DeviceLogEntry>[] = [
    {
      header: 'Time',
      accessor: 'created_at',
      Cell: (v: string) => <span className="text-xs text-neutral-400 cursor-default" title={formatTs(v)}>{timeAgo(v)}</span>,
    },
    {
      header: 'Uptime',
      accessor: 'device_ts_ms',
      Cell: (v: number | null) => (
        <span className="text-xs font-mono text-neutral-500">{formatDeviceTs(v)}</span>
      ),
    },
    {
      header: 'Level',
      accessor: 'level',
      Cell: (v: string) => (
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${LEVEL_BG[v] ?? ''} ${LEVEL_COLORS[v] ?? 'text-neutral-300'}`}>
          {v}
        </span>
      ),
    },
    {
      header: 'Tag',
      accessor: 'tag',
      Cell: (v: string) => <span className="font-mono text-xs text-purple-400">{v}</span>,
    },
    {
      header: 'Message',
      accessor: 'msg',
      Cell: (v: string) => (
        <span className="font-mono text-xs text-neutral-200 whitespace-pre-wrap break-all">{v}</span>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col gap-3 overflow-hidden">
      {/* ── Controls ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 shrink-0 flex-wrap">
        <label className="text-xs text-neutral-400">Device:</label>
        <select
          value={selectedDevice ?? ''}
          onChange={e => { setSelectedDevice(e.target.value || null); setLevelFilter(''); }}
          className="px-2 py-1 rounded pac-glass-input text-neutral-100 text-sm
                     focus:outline-none focus:ring-1 focus:ring-emerald-500"
        >
          <option value="">— select device —</option>
          {devices.map(d => (
            <option key={d.device_id} value={d.device_id}>
              {d.label ? `${d.label} (${d.device_id})` : d.device_id}
            </option>
          ))}
        </select>

        {selectedDevice && (
          <>
            <label className="text-xs text-neutral-400 ml-2">Level:</label>
            <select
              value={levelFilter}
              onChange={e => setLevelFilter(e.target.value)}
              className="px-2 py-1 rounded pac-glass-input text-neutral-100 text-sm
                         focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              <option value="">All levels</option>
              {LOG_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>

            <label className="text-xs text-neutral-400 ml-2">Limit:</label>
            <select
              value={limit}
              onChange={e => setLimit(Number(e.target.value))}
              className="px-2 py-1 rounded pac-glass-input text-neutral-100 text-sm
                         focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </>
        )}

        {isFetching && <span className="text-xs text-neutral-600 animate-pulse">refreshing…</span>}
      </div>

      {/* ── Level summary badges ──────────────────────────────────── */}
      {selectedDevice && logs.length > 0 && (
        <div className="shrink-0 flex items-center gap-2 flex-wrap">
          {LOG_LEVELS.filter(l => stats[l]).map(l => (
            <button
              key={l}
              onClick={() => setLevelFilter(levelFilter === l ? '' : l)}
              className={`px-2 py-0.5 rounded text-xs font-mono border transition
                ${levelFilter === l
                  ? `${LEVEL_BG[l]} ${LEVEL_COLORS[l]} border-current`
                  : 'border-white/5 text-neutral-500 hover:border-white/10'
                }`}
            >
              {l}: {stats[l]}
            </button>
          ))}
          <span className="text-xs text-neutral-600 ml-1">
            {logs.length} entries
          </span>
        </div>
      )}

      {/* ── Placeholder ───────────────────────────────────────────── */}
      {!selectedDevice && (
        <div className="flex-1 grid place-items-center text-neutral-600 text-sm">
          Select a device to view its log stream.
        </div>
      )}

      {/* ── Log table ─────────────────────────────────────────────── */}
      {selectedDevice && (
        <div className="flex-1 min-h-0 overflow-auto">
          {error ? (
            <div className="text-red-400 text-sm p-2">{error.message}</div>
          ) : (
            <MinimalisticTable<DeviceLogEntry>
              columns={columns}
              data={logs}
              noDataMessage={isLoading ? 'Loading device logs…' : 'No log entries for this device yet.'}
              showNewButton={false}
            />
          )}
        </div>
      )}
    </div>
  );
}
